// src/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import axios from 'axios';
import './Login.css';

const API_URL = import.meta.env.VITE_API_URL;

// ===== ユーティリティ =====
// ロール別のイベント一覧ページ（管理画面の入口。Sidebar.jsx と同じ対応）
const EVENT_LIST_PATH = {
  system: '/admin/eventlist',
  admin: '/admin/eventlist',
  operator: '/operator/eventlist',
  enduser: '/enduser/eventlist',
};

const resolveRedirectPath = (nextParam, userType) => {
  const safeNext = nextParam && nextParam.startsWith('/') ? nextParam : null;
  return safeNext || EVENT_LIST_PATH[userType] || EVENT_LIST_PATH.enduser;
};

// ===== コンポーネント =====
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, accessToken, userType } = useAuthStore();

  const lastId = localStorage.getItem('lastLoginID') || '';

  const [userAddress, setUserAddress] = useState(lastId);
  const [userPass, setUserPass] = useState('');
  const [remember, setRemember] = useState(!!lastId);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const canSubmit =
    userAddress.trim().length > 0 &&
    userPass.trim().length > 0 &&
    !submitting;

  // ログインID記憶
  useEffect(() => {
    if (remember && userAddress.trim()) {
      localStorage.setItem('lastLoginID', userAddress.trim());
    } else {
      localStorage.removeItem('lastLoginID');
    }
  }, [remember, userAddress]);

  const getNextPath = () => {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    return next && next.startsWith('/') ? next : null;
  };

  // ---- API エラーハンドリング ----
  const handleApiError = (error) => {
    console.error('status', error.response?.status);
    console.error('data', error.response?.data);

    if (error.response?.status === 429) {
      const retry = parseInt(error.response?.headers?.['retry-after'] ?? '10', 10);
      setServerError(`操作が集中しています。${retry}秒後に再試行できます。`);
    } else if (error.response?.status === 400) {
      setServerError('メールアドレス・パスワードをご確認ください。');
    } else if (error.message === 'Network Error') {
      setServerError('ネットワークエラーが発生しました。時間をおいて再度お試しください。');
    } else {
      setServerError('ログイン中にエラーが発生しました。再度お試しください。');
    }
  };

  // ---- フォーム送信 ----
  const handleLogin = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setServerError('');

    try {
      const res = await axios.post(`${API_URL}/api/auth/login/`, {
        userAddress,
        userPass,
      });

      if (res.data?.success) {
        const { WuserName, WuserType, access, refresh } = res.data;

        login({ access, refresh, WuserName, WuserType });

        navigate(resolveRedirectPath(getNextPath(), WuserType), { replace: true });
      } else {
        setServerError('メールアドレス・パスワードをご確認ください。');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // ログイン済みならログイン画面は表示せず管理画面へ
  if (accessToken) {
    return <Navigate to={resolveRedirectPath(getNextPath(), userType)} replace />;
  }

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="login-title">
        <h2 id="login-title" className="login-title">ログイン</h2>

        {serverError && (
          <div className="error-alert" role="alert">
            {serverError}
          </div>
        )}

        <form onSubmit={handleLogin} noValidate>
          {/* メールアドレス */}
          <div className="form-group">
            <label htmlFor="userAddress">メールアドレス</label>
            <input
              id="userAddress"
              name="userAddress"
              type="email"
              autoComplete="username"
              value={userAddress}
              onChange={(e) => setUserAddress(e.target.value)}
              placeholder="your@example.com"
              required
              aria-invalid={!!serverError}
            />
          </div>

          {/* パスワード */}
          <div className="form-group">
            <label htmlFor="userPass">パスワード</label>
            <div className="password-field">
              <input
                id="userPass"
                name="current-password"
                type={isPasswordVisible ? 'text' : 'password'}
                autoComplete="current-password"
                value={userPass}
                onChange={(e) => setUserPass(e.target.value)}
                placeholder="パスワード"
                required
                aria-invalid={!!serverError}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={isPasswordVisible ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {isPasswordVisible ? '隠す' : '表示'}
              </button>
            </div>
          </div>

          {/* オプション行 */}
          <div className="form-row">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              ログインIDを記憶する
            </label>
          </div>

          <button
            type="submit"
            className={`login-button ${submitting ? 'is-loading' : ''}`}
            disabled={!canSubmit}
          >
            {submitting ? 'ログイン中…' : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
